/**
 * The validators every stored setting passes through (§4.1, §7.1). Each answers
 * `null` for a value it will not keep, and the caller decides what that means:
 * the application layer falls back to its default, a workspace override goes
 * back to inheriting, and the account area refuses the document (§5.25).
 */
import { parseDockerfile } from './dockerfile';
import { selectionProblem, type HostService } from './hostservice';
import { EGRESS_DOMAIN, MAX_EGRESS_DOMAINS, MAX_NAMESERVERS, NAMESERVER } from './resolve';

export const CONFIG_SOURCES = ['host', 'none'];
export const AGENT_UPDATES = ['pin', 'latest'];
export const EGRESS_MODES = ['off', 'ask', 'auto'];

// The rule and the cap are `./resolve`'s so the fields that write these
// lists can refuse an entry rather than have it silently discarded here.
export const DOMAIN = EGRESS_DOMAIN;
export const MAX_DOMAINS = MAX_EGRESS_DOMAINS;

/** An image definition, not a program: past this it is a mistake, not a build. */
export const MAX_DOCKERFILE = 20_000;

/**
 * Each of these is a hole in the sandbox boundary (§5.9), so the cap is low on
 * purpose: a workspace needing a dozen host services is describing a different
 * architecture, not a dev setup.
 */
export const MAX_HOST_SERVICES = 8;
/** A connection URL, not a document. */
export const MAX_SERVICE_URL = 2_000;

/**
 * A Jira site as a bare host (§5.21). Anything with a scheme or a path is
 * reduced to the host it names rather than refused: pasting the URL out of the
 * browser is what a person actually does, and dropping it silently would be a
 * field that looks accepted and stays empty.
 */
export function jiraHost(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const bare = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '');
  return EGRESS_DOMAIN.test(bare) ? bare.slice(0, 200) : bare ? undefined : '';
}

export function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

export function int(value: unknown, [min, max]: readonly [number, number]): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function oneOf(value: unknown, allowed: readonly string[]): string | null {
  return typeof value === 'string' && allowed.includes(value) ? value : null;
}

/**
 * A path to a program Airlock will *execute* (§5.17), so it is clamped hard: an
 * absolute path, no shell metacharacters, no newline. It is still only a
 * candidate — the provider asks the file for its version before running it as an
 * agent — but a value that could carry a second command must not be stored at
 * all. Anything else falls back to "look where it usually is".
 */
export function binPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path) return '';
  // eslint-disable-next-line no-control-regex -- a control code in a path is not one.
  if (!path.startsWith('/') || path.length > 4096 || /[\x00-\x1f\x7f;&|`$<>"'\\*?]/.test(path)) return null;
  return path;
}

export function branch(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.trim();
  // git refname rules, kept narrow: no spaces, no `..`, no leading dash.
  if (!name || name.length > 200 || /\s|\.\.|^[-/]|[~^:?*[\\]|@\{/.test(name)) return null;
  return name;
}

/**
 * A Dockerfile is only stored once it parses into something buildable (§10.1):
 * a usable `FROM` and no instruction Airlock would have to ignore. The editor
 * reports the same issues as you type, so this is the backstop, not the first
 * time the user hears about it.
 */
export function dockerfile(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trimEnd();
  if (!text || text.length > MAX_DOCKERFILE) return null;
  const parsed = parseDockerfile(text);
  if (!parsed.image || parsed.issues.length) return null;
  return `${text}\n`;
}

/**
 * Mode A's selections (§5.9), clamped here like every other stored value: this
 * list becomes **egress rules**, so a malformed entry must not reach the policy
 * builder. Anything that fails `selectionProblem` is dropped rather than
 * repaired — a rule opening the host is not a thing to guess at.
 *
 * Deliberately not deduplicated by port: two selections on one port with
 * different variable names is a legitimate thing to want (two clients, two
 * names), and the rule they produce is the same rule either way.
 */
export function hostServices(value: unknown): HostService[] {
  if (!Array.isArray(value)) return [];
  const out: HostService[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const raw = entry as Partial<HostService>;
    const service: HostService = {
      id: typeof raw.id === 'string' && raw.id ? raw.id.slice(0, 40) : localId(),
      label: typeof raw.label === 'string' ? raw.label.trim().slice(0, 80) : '',
      port: typeof raw.port === 'number' ? Math.round(raw.port) : 0,
      envVar: typeof raw.envVar === 'string' ? raw.envVar.trim() : '',
      url: typeof raw.url === 'string' ? raw.url.trim().slice(0, MAX_SERVICE_URL) : ''
    };
    if (selectionProblem(service) || seen.has(service.id)) continue;
    seen.add(service.id);
    out.push(service);
    if (out.length >= MAX_HOST_SERVICES) break;
  }
  return out;
}

/**
 * The resolver list, or `null` when nothing usable was given — which the caller
 * reads as "use the default".
 *
 * Order is preserved on purpose: a machine on a restricted network puts its own
 * resolvers first and keeps the public ones behind them, and the runtime asks
 * them in the order it is given.
 */
export function nameservers(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const address = entry.trim();
    if (!NAMESERVER.test(address) || out.includes(address)) continue;
    out.push(address);
    if (out.length >= MAX_NAMESERVERS) break;
  }
  return out.length ? out : null;
}

export function domains(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const host = entry.trim().toLowerCase();
    if (!DOMAIN.test(host) || out.includes(host)) continue;
    out.push(host);
    if (out.length >= MAX_DOMAINS) break;
  }
  return out;
}

/**
 * An id for a selection that arrived without one. It has to be unique among the
 * eight a workspace may hold and nothing more — it names a row of a settings
 * page, not a credential — so no runtime's random API is worth reaching for.
 */
function localId(): string {
  return Math.random().toString(36).slice(2, 10).padEnd(8, '0');
}

/** At most this many terms, each at most this long — a list of names, not a dictionary (§7.5). */
export const MAX_PRIVACY_TERMS = 200;
export const MAX_PRIVACY_TERM_CHARS = 100;

/**
 * The terms masked out of what an agent is sent (§7.5): trimmed, inner spaces
 * collapsed, a duplicate in another case dropped, and anything under two
 * characters dropped — a single letter would mask every word it starts.
 */
export function privacyTerms(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const term = raw.trim().replace(/\s+/g, ' ').slice(0, MAX_PRIVACY_TERM_CHARS);
    if (term.length < 2 || seen.has(term.toLowerCase())) continue;
    seen.add(term.toLowerCase());
    out.push(term);
    if (out.length >= MAX_PRIVACY_TERMS) break;
  }
  return out;
}
